import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  BadgeCheck,
  BookOpen,
  Filter,
  Images,
  LayoutGrid,
  List,
  Loader2,
  Search,
  SlidersHorizontal,
  Star,
  Upload,
  X,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Alert } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DateInputBR } from '@components/DateInputBR';
import { Pagination } from '@components/Pagination';
import { TagInput } from '@components/TagInput';
import { MediaStatusBadge, MediaThumb } from '@components/media/MediaThumb';
import { MediaDetailDrawer } from '@components/media/MediaDetailDrawer';
import { MediaUploadDialog } from '@components/media/MediaUploadDialog';
import { DocContent } from '@components/help/DocContent';
import { Drawer } from '@components/Drawer';
import { usePermissions } from '@hooks/usePermissions';
import mediaService from '@services/mediaService';
import type { Pagination as PaginationInfo } from '@/types/activity';
import type { MediaAsset, MediaEvent, MediaStats, MediaTag, MediaTagCategory } from '@/types/media';
import {
  MEDIA_KIND_LABELS,
  MEDIA_ORIENTATION_LABELS,
  MEDIA_SORT_LABELS,
  MEDIA_SOURCE_LABELS,
  MEDIA_STATUS_LABELS,
} from '@/types/media';
import {
  MEDIA_SORTS,
  emptyMediaParams,
  hasMediaFilters,
  mediaParamsFromSearch,
  mediaParamsToSearch,
  toMediaFilter,
  type MediaListParams,
} from '@/utils/mediaFilters';
import { chipFromTag, type TagChip } from '@/utils/mediaTags';
import {
  formatBytes,
  formatDateBR,
  formatDateTimeBR,
  formatDuration,
  formatNumberBR,
} from '@/utils/formatters';

const PAGE_SIZE = 48;
const VIEW_KEY = 'media-library-view';
type ViewMode = 'grid' | 'list';

const readViewMode = (): ViewMode => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
};

const dims = (m: MediaAsset) => (m.width && m.height ? `${m.width}×${m.height}` : '');
const formatExt = (m: MediaAsset) => (m.extension || '').replace(/^\./, '').toUpperCase();

const StatCard: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode; tone?: string }> = ({
  label,
  value,
  hint,
  tone,
}) => (
  <div className="card px-4 py-3 min-w-[8rem]">
    <p className="text-small">{label}</p>
    <p className={clsx('text-xl font-semibold tabular-nums', tone ?? 'text-slate-900')}>{value}</p>
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

/**
 * Biblioteca de mídias: busca híbrida (tags + sinônimos + descrição + semântica), filtros na URL,
 * grade ou lista técnica, seleção múltipla com ações em lote e detalhe em `?media=<id>`.
 */
export const MediaLibraryView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkPermission } = usePermissions();
  const canUpload = checkPermission('media.upload').hasPermission;
  const canEdit = checkPermission('media.edit').hasPermission;
  const canRelease = checkPermission('media.release').hasPermission;
  const canBulk = canEdit || canRelease;

  const params = useMemo(() => mediaParamsFromSearch(searchParams), [searchParams]);
  const openId = Number(searchParams.get('media')) || null;

  const [searchInput, setSearchInput] = useState(params.search);
  const [showFilters, setShowFilters] = useState(() => hasMediaFilters({ ...params, search: '' }));
  const [viewMode, setViewMode] = useState<ViewMode>(readViewMode);
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [stats, setStats] = useState<MediaStats | null>(null);
  const [events, setEvents] = useState<MediaEvent[]>([]);
  const [allTags, setAllTags] = useState<MediaTag[]>([]);
  const [categories, setCategories] = useState<MediaTagCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [bulkAddTags, setBulkAddTags] = useState<TagChip[]>([]);
  const [bulkRemoveTags, setBulkRemoveTags] = useState<TagChip[]>([]);
  const [bulkEvent, setBulkEvent] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const setParams = useCallback(
    (changes: Partial<MediaListParams>, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const current = mediaParamsFromSearch(prev);
          const next = { ...current, ...changes, ...(resetPage && !('page' in changes) ? { page: 1 } : {}) };
          return mediaParamsToSearch(next, prev);
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setOpenId = useCallback(
    (id: number | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('media', String(id));
          else next.delete('media');
          return next;
        },
        { replace: !id },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  // Busca com debounce (a funcionalidade principal da tela).
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput.trim() !== params.search.trim()) setParams({ search: searchInput });
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchInput, params.search, setParams]);

  // Mantém o campo em sincronia quando a URL muda por fora (voltar do navegador, limpar filtros).
  useEffect(() => {
    setSearchInput((s) => (s.trim() === params.search.trim() ? s : params.search));
  }, [params.search]);

  const loadStats = useCallback(() => {
    mediaService.stats().then(setStats).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    loadStats();
    mediaService.events().then(setEvents).catch(() => setEvents([]));
    mediaService.tags().then(setAllTags).catch(() => setAllTags([]));
    mediaService.tagCategories().then(setCategories).catch(() => setCategories([]));
  }, [loadStats]);

  const filterKey = JSON.stringify(params);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mediaService.list(toMediaFilter(JSON.parse(filterKey) as MediaListParams, PAGE_SIZE));
      setItems(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelected(new Set());
  }, [filterKey]);

  const tagById = useMemo(() => new Map(allTags.map((t) => [t.id, t])), [allTags]);
  const filterTagChips: TagChip[] = params.tagIds.map((id) => {
    const t = tagById.get(id);
    return t ? chipFromTag(t) : { tag_id: id, category_id: 0, category_name: 'Tag', name: `#${id}` };
  });

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = items.length > 0 && items.every((m) => selected.has(m.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((m) => m.id)));

  const handleChanged = useCallback(
    (id: number, media: MediaAsset | null) => {
      setItems((prev) =>
        media
          ? prev.map((m) => (m.id === id ? { ...m, ...media, thumb_url: media.thumb_url ?? m.thumb_url } : m))
          : prev.filter((m) => m.id !== id),
      );
      loadStats();
    },
    [loadStats],
  );

  const runBulk = async (action: () => Promise<{ updated: number }>, label: string) => {
    setBulkBusy(true);
    try {
      const { updated } = await action();
      setBulkResult(`${updated} mídia(s) ${label}.`);
      setBulkAddTags([]);
      setBulkRemoveTags([]);
      setBulkEvent('');
      load();
      loadStats();
    } catch (err) {
      setError(err);
    } finally {
      setBulkBusy(false);
    }
  };

  const ids = [...selected];
  const applyBulkTags = () =>
    runBulk(
      () =>
        mediaService.bulk({
          media_ids: ids,
          add_tag_ids: bulkAddTags.map((t) => t.tag_id).filter((x): x is number => !!x),
          remove_tag_ids: bulkRemoveTags.map((t) => t.tag_id).filter((x): x is number => !!x),
        }),
      'atualizada(s)',
    );
  const applyBulkEvent = () =>
    runBulk(
      () =>
        mediaService.bulk(
          bulkEvent === 'clear' ? { media_ids: ids, clear_event: true } : { media_ids: ids, event_id: Number(bulkEvent) },
        ),
      bulkEvent === 'clear' ? 'sem evento' : 'vinculada(s) ao evento',
    );
  const applyBulkFlags = (flags: { is_released_for_customers?: boolean; is_preferred?: boolean }, label: string) =>
    runBulk(() => mediaService.bulkFlags({ media_ids: ids, ...flags }), label);

  const openIndex = openId ? items.findIndex((m) => m.id === openId) : -1;
  const filtersActive = hasMediaFilters({ ...params, search: '' });

  const sortValue = params.sortBy || (params.search ? 'relevance' : 'created_at');
  const sortDir = params.sortDirection || (sortValue === 'file_name' ? 'asc' : 'desc');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Images size={20} className="text-slate-500" /> Biblioteca de mídias
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" icon={<BookOpen />} onClick={() => setHelpOpen(true)}>
            Como funciona
          </Button>
          {canUpload && (
            <Button variant="primary" size="sm" icon={<Upload />} onClick={() => setUploadOpen(true)}>
              Enviar arquivos
            </Button>
          )}
        </div>
      </div>

      <Drawer
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        size="xl"
        title={
          <span className="flex items-center gap-2">
            <BookOpen size={18} className="text-emerald-600" /> Biblioteca — como funciona
          </span>
        }
      >
        <DocContent rota="/media" className="" />
      </Drawer>

      {/* Totais */}
      {stats && (
        <div className="grid grid-cols-2 sm:flex gap-3 sm:overflow-x-auto pb-1">
          <StatCard label="Total" value={stats.total.toLocaleString('pt-BR')} hint={formatBytes(stats.total_bytes)} />
          <StatCard label="Imagens" value={stats.images.toLocaleString('pt-BR')} />
          <StatCard label="Vídeos" value={stats.videos.toLocaleString('pt-BR')} />
          <StatCard
            label="Em processamento"
            value={(stats.pending + stats.processing).toLocaleString('pt-BR')}
            hint={`${stats.pending} na fila`}
            tone={stats.pending + stats.processing > 0 ? 'text-amber-600' : undefined}
          />
          <StatCard label="Com erro" value={stats.error.toLocaleString('pt-BR')} tone={stats.error > 0 ? 'text-red-600' : undefined} />
          <StatCard label="Liberadas" value={stats.released.toLocaleString('pt-BR')} tone="text-emerald-700" />
          <StatCard label="Preferenciais" value={stats.preferred.toLocaleString('pt-BR')} tone="text-amber-600" />
          <Link to="/media/duplicates" className="block">
            <StatCard
              label="Duplicadas pendentes"
              value={stats.duplicates_pending.toLocaleString('pt-BR')}
              hint="Revisar →"
              tone={stats.duplicates_pending > 0 ? 'text-violet-700' : undefined}
            />
          </Link>
        </div>
      )}

      {/* Busca + filtros */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[16rem]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input-field pl-10 py-2.5 text-base"
              placeholder="Buscar: caixa de couro savana, evento Palácio Tangará..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoFocus
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Limpar busca"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((s) => !s)}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm',
              filtersActive ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-300 text-slate-700 hover:bg-slate-50',
            )}
          >
            <SlidersHorizontal size={16} /> Filtros{filtersActive ? ' (ativos)' : ''}
          </button>
          <div className="flex items-center gap-1">
            <select
              className="select-field w-auto"
              value={sortValue}
              onChange={(e) => setParams({ sortBy: e.target.value as MediaListParams['sortBy'], sortDirection: '' })}
              aria-label="Ordenar por"
            >
              {MEDIA_SORTS.filter((s) => s !== 'relevance' || params.search).map((s) => (
                <option key={s} value={s}>
                  {MEDIA_SORT_LABELS[s]}
                </option>
              ))}
            </select>
            {sortValue !== 'relevance' && (
              <button
                type="button"
                onClick={() => setParams({ sortBy: sortValue, sortDirection: sortDir === 'asc' ? 'desc' : 'asc' })}
                className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                title={sortDir === 'asc' ? 'Crescente' : 'Decrescente'}
              >
                {sortDir === 'asc' ? <ArrowUpNarrowWide size={16} /> : <ArrowDownWideNarrow size={16} />}
              </button>
            )}
          </div>
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={clsx('p-2', viewMode === 'grid' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50')}
              title="Grade"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={clsx('p-2 border-l border-slate-300', viewMode === 'list' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50')}
              title="Lista técnica"
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 pt-2 border-t border-slate-100">
            <div className="sm:col-span-2">
              <label className="text-label block mb-1">Tags (todas)</label>
              <TagInput
                value={filterTagChips}
                onChange={(chips) => setParams({ tagIds: chips.map((c) => c.tag_id).filter((x): x is number => !!x) })}
                placeholder="Filtrar por tag…"
              />
            </div>
            <div>
              <label className="text-label block mb-1">Tipo</label>
              <select className="select-field" value={params.kind} onChange={(e) => setParams({ kind: e.target.value as MediaListParams['kind'] })}>
                <option value="">Todos</option>
                {(Object.keys(MEDIA_KIND_LABELS) as (keyof typeof MEDIA_KIND_LABELS)[]).map((k) => (
                  <option key={k} value={k}>
                    {MEDIA_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-label block mb-1">Evento</label>
              <select className="select-field" value={params.eventId} onChange={(e) => setParams({ eventId: e.target.value })}>
                <option value="">Todos</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-label block mb-1">Data de</label>
              <DateInputBR className="input-field w-full" value={params.dateFrom} onChange={(v) => setParams({ dateFrom: v })} />
            </div>
            <div>
              <label className="text-label block mb-1">Data até</label>
              <DateInputBR
                className="input-field w-full"
                value={params.dateTo}
                min={params.dateFrom || undefined}
                onChange={(v) => setParams({ dateTo: v })}
              />
            </div>
            <div>
              <label className="text-label block mb-1">Liberada para clientes</label>
              <select className="select-field" value={params.released} onChange={(e) => setParams({ released: e.target.value as MediaListParams['released'] })}>
                <option value="">Todas</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
            <div>
              <label className="text-label block mb-1">Status</label>
              <select className="select-field" value={params.status} onChange={(e) => setParams({ status: e.target.value as MediaListParams['status'] })}>
                <option value="">Todos</option>
                {(Object.keys(MEDIA_STATUS_LABELS) as (keyof typeof MEDIA_STATUS_LABELS)[]).map((s) => (
                  <option key={s} value={s}>
                    {MEDIA_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-label block mb-1">Orientação</label>
              <select
                className="select-field"
                value={params.orientation}
                onChange={(e) => setParams({ orientation: e.target.value as MediaListParams['orientation'] })}
              >
                <option value="">Todas</option>
                {(Object.keys(MEDIA_ORIENTATION_LABELS) as (keyof typeof MEDIA_ORIENTATION_LABELS)[]).map((o) => (
                  <option key={o} value={o}>
                    {MEDIA_ORIENTATION_LABELS[o]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-label block mb-1">Origem</label>
              <select className="select-field" value={params.source} onChange={(e) => setParams({ source: e.target.value as MediaListParams['source'] })}>
                <option value="">Todas</option>
                {(Object.keys(MEDIA_SOURCE_LABELS) as (keyof typeof MEDIA_SOURCE_LABELS)[]).map((s) => (
                  <option key={s} value={s}>
                    {MEDIA_SOURCE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox-field"
                  checked={params.preferred}
                  onChange={(e) => setParams({ preferred: e.target.checked })}
                />
                Só preferenciais
              </label>
            </div>
            {filtersActive && (
              <div className="flex items-end pb-1">
                <button
                  type="button"
                  onClick={() =>
                    // Sem filtro e sem busca a lista volta à ordem padrão: data de envio, mais recentes primeiro.
                    setParams(
                      params.search.trim()
                        ? { ...emptyMediaParams(), search: params.search, sortBy: params.sortBy, sortDirection: params.sortDirection }
                        : emptyMediaParams(),
                    )
                  }
                  className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                >
                  <X size={14} /> Limpar filtros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {bulkResult && (
        <Alert variant="success" onClose={() => setBulkResult(null)}>
          {bulkResult}
        </Alert>
      )}

      {/* Ações em lote */}
      {canBulk && selected.size > 0 && (
        <div className="card p-3 space-y-3 border-emerald-200 bg-emerald-50">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-emerald-800">{selected.size} selecionada(s)</span>
            <button type="button" className="text-sm text-slate-500 hover:text-slate-800" onClick={() => setSelected(new Set())}>
              Limpar seleção
            </button>
            {canRelease && (
              <div className="flex flex-wrap gap-2 ml-auto">
                <Button variant="outline" size="sm" icon={<BadgeCheck />} disabled={bulkBusy} onClick={() => applyBulkFlags({ is_released_for_customers: true }, 'liberada(s) para clientes')}>
                  Liberar
                </Button>
                <Button variant="outline" size="sm" disabled={bulkBusy} onClick={() => applyBulkFlags({ is_released_for_customers: false }, 'bloqueada(s) para clientes')}>
                  Bloquear
                </Button>
                <Button variant="outline" size="sm" icon={<Star />} disabled={bulkBusy} onClick={() => applyBulkFlags({ is_preferred: true }, 'marcada(s) como preferencial')}>
                  Preferencial
                </Button>
                <Button variant="outline" size="sm" disabled={bulkBusy} onClick={() => applyBulkFlags({ is_preferred: false }, 'desmarcada(s) como preferencial')}>
                  Tirar preferencial
                </Button>
              </div>
            )}
          </div>
          {canEdit && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Adicionar tags</label>
                <TagInput value={bulkAddTags} onChange={setBulkAddTags} placeholder="Tags a adicionar…" categories={categories} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Remover tags</label>
                <TagInput value={bulkRemoveTags} onChange={setBulkRemoveTags} placeholder="Tags a remover…" categories={categories} />
              </div>
              <Button
                variant="primary"
                size="sm"
                loading={bulkBusy}
                disabled={bulkAddTags.length === 0 && bulkRemoveTags.length === 0}
                onClick={applyBulkTags}
              >
                Aplicar tags
              </Button>
              <div className="lg:col-span-2">
                <label className="text-xs font-medium text-slate-600 block mb-1">Evento</label>
                <select className="select-field" value={bulkEvent} onChange={(e) => setBulkEvent(e.target.value)}>
                  <option value="">Escolha…</option>
                  <option value="clear">Remover o evento</option>
                  {events
                    .filter((ev) => ev.is_active)
                    .map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.name}
                      </option>
                    ))}
                </select>
              </div>
              <Button variant="primary" size="sm" loading={bulkBusy} disabled={!bulkEvent} onClick={applyBulkEvent}>
                Aplicar evento
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Resultados */}
      {viewMode === 'grid' ? (
        <div className="space-y-3">
          {canBulk && items.length > 0 && (
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" className="checkbox-field" checked={allSelected} onChange={toggleAll} />
              Selecionar todas desta página
            </label>
          )}
          {loading && items.length === 0 && (
            <p className="text-center text-slate-400 py-12">
              <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
            </p>
          )}
          {!loading && items.length === 0 && <EmptyState />}
          <div
            className={clsx(
              'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3',
              loading && 'opacity-60',
            )}
          >
            {items.map((m) => (
              <div
                key={m.id}
                className={clsx(
                  'group card overflow-hidden cursor-pointer hover:shadow-card-hover transition-shadow',
                  selected.has(m.id) && 'ring-2 ring-emerald-500',
                )}
                onClick={() => setOpenId(m.id)}
              >
                <div className="relative">
                  <MediaThumb media={m} className="aspect-square" />
                  {canBulk && (
                    <label
                      className={clsx(
                        'absolute bottom-1.5 left-1.5 rounded bg-white/90 p-1 shadow transition-opacity',
                        selected.has(m.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input type="checkbox" className="checkbox-field" checked={selected.has(m.id)} onChange={() => toggleOne(m.id)} />
                    </label>
                  )}
                </div>
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-800 truncate" title={m.title || m.original_file_name}>
                    {m.title || m.original_file_name}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {[dims(m), formatBytes(m.size_bytes)].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {pagination && items.length > 0 && (
            <div className="card overflow-hidden">
              <Pagination
                page={pagination.page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total_items}
                onChange={(page) => setParams({ page }, false)}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="table-container bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  {canBulk && (
                    <th className="w-10">
                      <input type="checkbox" className="checkbox-field" checked={allSelected} onChange={toggleAll} />
                    </th>
                  )}
                  <th>Miniatura</th>
                  <th>Nome / título</th>
                  <th>Tipo</th>
                  <th>Formato</th>
                  <th className="text-right">Tamanho</th>
                  <th>Dimensões</th>
                  <th className="text-right">MP</th>
                  <th>Duração</th>
                  <th className="text-right">FPS</th>
                  <th>Codec</th>
                  <th>Data do conteúdo</th>
                  <th>Criado em</th>
                  <th>Status</th>
                  <th>Liberada</th>
                  <th>Preferencial</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 && (
                  <tr>
                    <td colSpan={16} className="table-cell text-center text-slate-400">
                      <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={16}>
                      <EmptyState />
                    </td>
                  </tr>
                )}
                {items.map((m) => (
                  <tr
                    key={m.id}
                    className={clsx('table-row cursor-pointer', selected.has(m.id) && 'bg-emerald-50/60', loading && 'opacity-60')}
                    onClick={() => setOpenId(m.id)}
                  >
                    {canBulk && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="checkbox-field" checked={selected.has(m.id)} onChange={() => toggleOne(m.id)} />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <MediaThumb media={m} className="w-14 h-14 rounded" showBadges={false} />
                    </td>
                    <td className="px-3 py-2 text-sm max-w-[16rem]">
                      <p className="font-medium text-slate-800 truncate" title={m.title}>
                        {m.title || '—'}
                      </p>
                      <p className="text-xs text-slate-500 truncate" title={m.original_file_name}>
                        {m.original_file_name}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-sm">{MEDIA_KIND_LABELS[m.kind]}</td>
                    <td className="px-3 py-2 text-sm font-mono">{formatExt(m)}</td>
                    <td className="px-3 py-2 text-sm text-right whitespace-nowrap tabular-nums">{formatBytes(m.size_bytes)}</td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap tabular-nums">{dims(m) || '—'}</td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{formatNumberBR(m.megapixels) || '—'}</td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap tabular-nums">
                      {m.duration_seconds !== undefined ? (
                        <>
                          {formatDuration(m.duration_seconds)}
                          <span className="text-xs text-slate-400 ml-1">({formatNumberBR(m.duration_seconds)} s)</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums">{formatNumberBR(m.frame_rate, 2) || '—'}</td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      {[m.video_codec, m.audio_codec].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">{formatDateBR(m.content_date) || '—'}</td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">{formatDateTimeBR(m.created_at)}</td>
                    <td className="px-3 py-2 text-sm">
                      <MediaStatusBadge status={m.status} always />
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {m.is_released_for_customers ? <BadgeCheck size={16} className="text-emerald-600" /> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {m.is_preferred ? <Star size={16} className="text-amber-500 fill-amber-400" /> : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              totalItems={pagination.total_items}
              onChange={(page) => setParams({ page }, false)}
            />
          )}
        </div>
      )}

      <MediaDetailDrawer
        mediaId={openId}
        onClose={() => setOpenId(null)}
        onChanged={handleChanged}
        events={events}
        categories={categories}
        onPrev={openIndex > 0 ? () => setOpenId(items[openIndex - 1].id) : undefined}
        onNext={openIndex >= 0 && openIndex < items.length - 1 ? () => setOpenId(items[openIndex + 1].id) : undefined}
      />

      {canUpload && (
        <MediaUploadDialog
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            load();
            loadStats();
          }}
        />
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="text-center py-12">
    <Filter size={32} className="text-slate-300 mx-auto mb-2" />
    <p className="text-slate-600 font-medium">Nenhuma mídia encontrada</p>
    <p className="text-small">
      Tente outras palavras (sinônimos como “Savana” e “nude” também valem) ou ajuste os filtros.
    </p>
  </div>
);
